import { useEffect, useState, type FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../utils/api.js";

interface SubmissionField {
  name: string;
  label: string;
  type: "text" | "textarea" | "url";
  required: boolean;
  placeholder: string;
}

interface AssignmentData {
  id: number;
  title: string;
  pdfPath: string;
  notes: string | null;
  timelineDays: number;
  submissionFields: SubmissionField[];
}

const FIELD_TYPES = [
  { value: "url", label: "URL" },
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
] as const;

function emptyField(): SubmissionField {
  return { name: "", label: "", type: "url", required: true, placeholder: "" };
}

export function AssignmentForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [timelineDays, setTimelineDays] = useState(7);
  const [submissionFields, setSubmissionFields] = useState<SubmissionField[]>([]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [existingPdf, setExistingPdf] = useState<string | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .get<AssignmentData>(`/assignment-library/${id}`)
      .then((data) => {
        setTitle(data.title);
        setNotes(data.notes ?? "");
        setTimelineDays(data.timelineDays);
        setSubmissionFields(data.submissionFields ?? []);
        setExistingPdf(data.pdfPath);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load assignment"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const validFields = submissionFields.filter((f) => f.name.trim() && f.label.trim());

    try {
      if (isEdit) {
        await api.put(`/assignment-library/${id}`, {
          title,
          notes: notes || null,
          timelineDays,
          submissionFields: validFields,
        });
        navigate("/assignments?success=Assignment+updated");
      } else {
        if (!pdfFile) {
          setError("PDF file is required");
          setSaving(false);
          return;
        }
        const formData = new FormData();
        formData.append("title", title);
        formData.append("timelineDays", String(timelineDays));
        formData.append("pdf", pdfFile);
        if (notes) formData.append("notes", notes);
        if (validFields.length > 0)
          formData.append("submissionFields", JSON.stringify(validFields));

        const resp = await fetch("/api/v1/assignment-library", {
          method: "POST",
          body: formData,
          credentials: "include",
        });
        if (!resp.ok) {
          const data = (await resp.json()) as { message?: string };
          throw new Error(data.message ?? "Failed to create assignment");
        }
        navigate("/assignments?success=Assignment+created");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400 animate-pulse">Loading assignment...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/assignments"
          className="text-sm text-primary-700 hover:text-primary-800 mb-2 block"
        >
          &larr; All Assignments
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? "Edit Assignment" : "New Assignment"}
        </h1>
      </div>

      {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <form
        onSubmit={handleSubmit}
        className="space-y-5 rounded-lg border border-gray-200 bg-white p-6"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Title <span className="text-red-500">*</span>
            {parsing && (
              <span className="ml-2 text-xs text-gray-400 animate-pulse">
                Extracting from PDF...
              </span>
            )}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="e.g. Infrastructure Reliability Challenge"
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Timeline (days) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            max={90}
            value={timelineDays}
            onChange={(e) => setTimelineDays(Number(e.target.value) || 7)}
            className="input w-24"
          />
          <p className="text-xs text-gray-400 mt-1">
            Candidates will have this many days to complete the assignment
          </p>
        </div>

        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              PDF File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={async (e) => {
                const file = e.target.files?.[0] ?? null;
                setPdfFile(file);
                if (file && !title.trim()) {
                  setParsing(true);
                  try {
                    const formData = new FormData();
                    formData.append("file", file);
                    const resp = await fetch("/api/v1/assignment-library/parse-pdf", {
                      method: "POST",
                      body: formData,
                      credentials: "include",
                    });
                    if (resp.ok) {
                      const data = (await resp.json()) as { title: string | null };
                      if (data.title && !title.trim()) setTitle(data.title);
                    }
                  } catch {
                    /* title extraction is best-effort */
                  } finally {
                    setParsing(false);
                  }
                }
              }}
              className="block w-full text-sm text-gray-500 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
            />
          </div>
        )}

        {isEdit && existingPdf && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">PDF File</label>
            <a
              href={`/uploads/${existingPdf}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary-700 hover:text-primary-800"
            >
              Preview PDF
            </a>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Internal notes about this assignment (not visible to candidates)"
            className="input"
          />
          <p className="text-xs text-gray-400 mt-1">Employee-only, not visible to applicants</p>
        </div>

        <div className="border-t border-gray-200 pt-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Submission Fields</label>
              <p className="text-xs text-gray-400 mt-0.5">
                What the candidate fills in when submitting. If none are configured, defaults (Repo
                URL + Notes) will be shown.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSubmissionFields([...submissionFields, emptyField()])}
              className="rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 transition-colors"
            >
              + Add Field
            </button>
          </div>

          {submissionFields.length > 0 && (
            <div className="space-y-3">
              {submissionFields.map((field, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr_1fr_100px_auto_auto] gap-2 items-start"
                >
                  <input
                    type="text"
                    placeholder="Field name (e.g. repo_url)"
                    value={field.name}
                    onChange={(e) => {
                      const next = [...submissionFields];
                      next[idx] = { ...field, name: e.target.value };
                      setSubmissionFields(next);
                    }}
                    className="input text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Label (e.g. GitHub Repo URL)"
                    value={field.label}
                    onChange={(e) => {
                      const next = [...submissionFields];
                      next[idx] = { ...field, label: e.target.value };
                      setSubmissionFields(next);
                    }}
                    className="input text-sm"
                  />
                  <select
                    value={field.type}
                    onChange={(e) => {
                      const next = [...submissionFields];
                      next[idx] = {
                        ...field,
                        type: e.target.value as "text" | "textarea" | "url",
                      };
                      setSubmissionFields(next);
                    }}
                    className="input text-sm"
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 pt-2 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => {
                        const next = [...submissionFields];
                        next[idx] = { ...field, required: e.target.checked };
                        setSubmissionFields(next);
                      }}
                      className="rounded border-gray-300"
                    />
                    Required
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setSubmissionFields(submissionFields.filter((_, i) => i !== idx))
                    }
                    className="text-gray-400 hover:text-red-600 pt-2 text-sm"
                    title="Remove field"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving || !title.trim() || (!isEdit && !pdfFile)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] disabled:opacity-50 transition-colors"
          >
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Assignment"}
          </button>
          <Link to="/assignments" className="text-sm text-gray-500 hover:text-gray-700">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
